import { FC, useCallback, useState } from "react";
import { Button, Card } from "@ventumsoftware/react-toolkit";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import {
  MailDomain,
  TransactionalMailDomainDeploymentDetails,
  TransactionalMailSmtpUser,
} from "@hostingbv/lib/src/api/partner/models/MailDomain";
import {
  faPlus,
  faRotateRight,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Paragraph } from "../../../lib/src/components/Paragraph";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePermissions } from "../../../lib/src/auth/permissions";
import { Editor } from "../../../lib/src/components/dns-editor/Editor";
import { Domain } from "../../../lib/src/api/partner/models/Domain";
import { DNSRecord } from "../../../lib/src/api/partner/models/DNSZone";
import { useModal } from "../../../lib/src/components/layout/ModalProvider";
import { useApiClient } from "@hostingbv/lib/src/api";
import { config } from "../config";
import { AddTransactionalMailAliasModal } from "../modals/AddTransactionalMailAliasModal";
import { AddTransactionalMailUserModal } from "../modals/AddTransactionalMailUserModal";
import { DeleteTransactionalMailUserModal } from "../modals/DeleteTransactionalMailUserModal";
import { DeleteTransactionalMailAliasModal } from "../modals/DeleteTransactionalMailAliasModal";
import { useRealtimeDeployments } from "./deployments/RealtimeDeployments";
import { useRefresh } from "../../../lib/src/components/RefreshController";
import { ContextMenu } from "../../../lib/src/components/content/ContextMenu";
import { ContractsOverview } from "./ImapMailDomainDetails";

export const TransactionalMailDomainDetails: FC<{ mailDomain: MailDomain }> = ({
  mailDomain,
}) => {
  const deploymentDetails =
    mailDomain.deploymentDetails instanceof
    TransactionalMailDomainDeploymentDetails
      ? mailDomain.deploymentDetails
      : null;

  return (
    <>
      <SectionHeader>Credentials</SectionHeader>
      <CredentialsOverview
        mailDomain={mailDomain}
        deploymentDetails={deploymentDetails}
      />

      <SectionHeader>Alias domains</SectionHeader>
      <AliasDomainsOverview
        mailDomain={mailDomain}
        deploymentDetails={deploymentDetails}
      />

      <SectionHeader>DNS records</SectionHeader>
      <div className={"mb-8"}>
        <Card title={"DNS"}>
          <Paragraph>
            Add the following DNS records to the DNS of the domain name to
            enable transactional mail.
          </Paragraph>
          <div className={"space-y-6"}>
            {groupRecordsByDomain(
              [
                deploymentDetails?.domain ?? mailDomain.displayName,
                ...(deploymentDetails?.aliasDomains ?? []),
              ],
              deploymentDetails?.dnsRecords ?? [],
            ).map(({ domain, records }) => (
              <Editor
                key={domain}
                domain={{ displayName: domain } as Domain}
                records={records.map((record, index): DNSRecord => ({
                  id: String(index),
                  type: "CNAME",
                  name: record.name,
                  content: record.target,
                  ttl: 3600,
                  proxied: false,
                }))}
                readonlyMode={true}
                onSave={() => {}}
              />
            ))}
          </div>
          <RefreshRecordsButton mailDomain={mailDomain} />
        </Card>
      </div>

      <SectionHeader>Contracts</SectionHeader>
      <ContractsOverview mailDomain={mailDomain} />
    </>
  );
};

// The DNS editor shows record names relative to a single domain, so records are
// split per head/alias domain. Longest match wins so "a.example.com" records
// don't end up under "example.com".
function groupRecordsByDomain<T extends { name: string }>(
  domains: string[],
  records: T[],
): { domain: string; records: T[] }[] {
  const byLength = [...domains].sort((a, b) => b.length - a.length);
  return domains
    .map((domain) => ({
      domain,
      records: records.filter(
        (record) =>
          byLength.find(
            (d) => record.name === d || record.name.endsWith(`.${d}`),
          ) === domain,
      ),
    }))
    .filter((group) => group.records.length > 0);
}

function CredentialsOverview(props: {
  mailDomain: MailDomain;
  deploymentDetails: TransactionalMailDomainDeploymentDetails | null;
}) {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const smtpUsers = props.deploymentDetails?.smtpUsers ?? [];
  const { open: openAddModal } = useModal({
    title: "Add credential",
    body: <AddTransactionalMailUserModal domain={props.mailDomain} />,
  });
  return (
    <div
      className={
        "mb-12 bg-white text-black rounded-sm my-4 border border-slate-100 p-2"
      }
    >
      <table className={"w-full table"}>
        <tbody>
          {smtpUsers.map((user, k) => (
            <CredentialsOverviewTableRow
              key={k}
              user={user}
              mailDomain={props.mailDomain}
            />
          ))}
        </tbody>
      </table>
      {can("edit", "mail", props.mailDomain.permissions) && (
        <button
          className={
            "hover:bg-brand-50 text-brand-700 w-full text-sm px-4 py-3 rounded-b text-left"
          }
          onClick={() => openAddModal()}
        >
          <FontAwesomeIcon icon={faPlus} className={"mr-2"} />
          <span className={"font-medium"}>Add credential</span>
        </button>
      )}
    </div>
  );
}

const CredentialsOverviewTableRow: FC<{
  user: TransactionalMailSmtpUser;
  mailDomain: MailDomain;
}> = ({ user, mailDomain }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { open: openDeleteModal } = useModal({
    title: "Delete credential",
    body: <DeleteTransactionalMailUserModal domain={mailDomain} user={user} />,
  });
  const options = [
    {
      label: "Delete",
      icon: faTrash,
      onClick: () => {
        openDeleteModal();
      },
    },
  ];
  return (
    <tr className={"text-sm h-12"}>
      <th className={"px-4 text-left w-full"}>{user.emailAddress}</th>
      {can("delete", "mail", mailDomain.permissions) && (
        <td className={"px-4 text-right"}>
          <ContextMenu options={options} />
        </td>
      )}
    </tr>
  );
};

function AliasDomainsOverview(props: {
  mailDomain: MailDomain;
  deploymentDetails: TransactionalMailDomainDeploymentDetails | null;
}) {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const aliasDomains = props.deploymentDetails?.aliasDomains ?? [];
  const { open: openAddModal } = useModal({
    title: "Add alias domain",
    body: <AddTransactionalMailAliasModal domain={props.mailDomain} />,
  });
  return (
    <div
      className={
        "mb-12 bg-white text-black rounded-sm my-4 border border-slate-100 p-2"
      }
    >
      <table className={"w-full table"}>
        <tbody>
          {aliasDomains.map((aliasDomain) => (
            <AliasDomainsOverviewTableRow
              key={aliasDomain}
              aliasDomain={aliasDomain}
              mailDomain={props.mailDomain}
            />
          ))}
        </tbody>
      </table>
      {can("edit", "mail", props.mailDomain.permissions) && (
        <button
          className={
            "hover:bg-brand-50 text-brand-700 w-full text-sm px-4 py-3 rounded-b text-left"
          }
          onClick={() => openAddModal()}
        >
          <FontAwesomeIcon icon={faPlus} className={"mr-2"} />
          <span className={"font-medium"}>Add alias domain</span>
        </button>
      )}
    </div>
  );
}

const AliasDomainsOverviewTableRow: FC<{
  aliasDomain: string;
  mailDomain: MailDomain;
}> = ({ aliasDomain, mailDomain }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { open: openDeleteModal } = useModal({
    title: "Delete alias domain",
    body: (
      <DeleteTransactionalMailAliasModal
        domain={mailDomain}
        aliasDomain={aliasDomain}
      />
    ),
  });
  const options = [
    {
      label: "Delete",
      icon: faTrash,
      onClick: () => {
        openDeleteModal();
      },
    },
  ];
  return (
    <tr className={"text-sm h-12"}>
      <th className={"px-4 text-left w-full"}>{aliasDomain}</th>
      {can("edit", "mail", mailDomain.permissions) && (
        <td className={"px-4 text-right"}>
          <ContextMenu options={options} />
        </td>
      )}
    </tr>
  );
};

function RefreshRecordsButton(props: { mailDomain: MailDomain }) {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!;
  const refresh = useRefresh();
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const doRefresh = useCallback(async () => {
    setRefreshing(true);
    setError("");
    try {
      const result = await partnerClient.refreshTransactionalMailDomainRecords(
        props.mailDomain.id,
      );
      if (!result.success) {
        setError(result.error);
        return;
      }
      await awaitNextDeploymentFinished(result.data.id);
      refresh();
    } finally {
      setRefreshing(false);
    }
  }, [props.mailDomain]);

  if (!can("edit", "mail", props.mailDomain.permissions)) {
    return null;
  }

  return (
    <div className={"mt-4"}>
      <Button
        size={"sm"}
        type={"secondary"}
        text={refreshing ? "Refreshing..." : "Refresh records"}
        icon={faRotateRight}
        disabled={refreshing}
        onClick={doRefresh}
      />
      {error.length > 0 && (
        <div className={"font-medium text-red-600 mt-2"}>{error}</div>
      )}
    </div>
  );
}
